
import React, { useEffect, useMemo, useState } from 'react';
import { Typography, Box, Button } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import CustomizedDialogs from './AddInventory';
import CommodityGrid from './Datagrid';

// ---------- EXPIRY FILTERS ----------

const EXPIRY_KEYS = [
  { key: 'ALL',     label: 'All' },
  { key: 'EXP_6M',  label: '6 months - 3 months' },
  { key: 'EXP_3M',  label: '3 months - 1 month' },
  { key: 'EXP_1M',  label: '1 month - 1 week' },
  { key: 'EXP_1W',  label: '1 week - 1 day' },
  { key: 'EXP_1D',  label: 'Tomorrow' },
  { key: 'EXPIRED', label: 'Expired' },
];

// map expiry key → API filter params
const expiryKeyToFilter = (key) => {
  switch (key) {
    case 'EXP_1D':  return { expiryGteDays: 1, expiryLteDays: 1 };
    case 'EXP_1W':  return { expiryLteDays: 7 };
    case 'EXP_1M':  return { expiryLteDays: 30 };
    case 'EXP_3M':  return { expiryLteDays: 90 };
    case 'EXP_6M':  return { expiryLteDays: 180 };
    case 'EXPIRED': return { expired: true };
    default:        return {};
  }
};

// read ?filter= from URL for deep-links
const queryToExpiryKey = (search) => {
  const q = new URLSearchParams(search);
  const f = (q.get('filter') || '').toLowerCase();
  if (f === '1day')    return 'EXP_1D';
  if (f === '7days') return 'EXP_1W';
  if (f === '30days')  return 'EXP_1M';
  if (f === '90days')  return 'EXP_3M';
  if (f === '180days') return 'EXP_6M';
  if (f === 'expired') return 'EXPIRED';
  return 'ALL';
};

const queryToStockKey = (search) => {
  const q = new URLSearchParams(search);
  const s = (q.get('stock') || '').toLowerCase();
  if (s === 'low') return 'LOW';
  if (s === 'critical') return 'CRIT';
  if (s === 'out') return 'OUT';
  return 'STOCK_ALL';
};


// ---------- STOCK FILTERS ----------

const STOCK_KEYS = [
  { key: 'STOCK_ALL', label: 'All' },
  { key: 'LOW',       label: 'Low Stock' },
  { key: 'CRIT',      label: 'Critically Low' },
  { key: 'OUT',       label: 'Out of Stock' },
  
  
];

const Inventory = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [restockRow, setRestockRow] = useState(null);
  const [typeKey, setTypeKey] = useState('TYPE_ALL');

  // expiry filter (from URL)
  const [expiryKey, setExpiryKey] = useState(() => queryToExpiryKey(location.search));
  // stock filter (local only for now)
  const [stockKey, setStockKey] = useState(() => queryToStockKey(location.search));


  // keep expiryKey in sync with URL changes (back/forward)
  useEffect(() => {
    setExpiryKey(queryToExpiryKey(location.search));
    setStockKey(queryToStockKey(location.search));
  }, [location.search]);

  // when user clicks an expiry filter button
  const handleExpiryChange = (newKey) => {
    setExpiryKey(newKey);
    const params = new URLSearchParams(location.search);

    switch (newKey) {
      case 'EXP_1D':  params.set('filter', '1day');    break;
      case 'EXP_1W': params.set('filter', '7days');    break; 
      case 'EXP_1M':  params.set('filter', '30days');  break;
      case 'EXP_3M':  params.set('filter', '90days');  break;
      case 'EXP_6M':  params.set('filter', '180days'); break;
      case 'EXPIRED': params.set('filter', 'expired'); break;
      default:        params.delete('filter');         break;
    }

    navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });
  };

  // when user clicks a stock filter button
  const handleStockChange = (newKey) => {
  setStockKey(newKey);

  const params = new URLSearchParams(location.search);

  switch (newKey) {
    case 'LOW':  params.set('stock', 'low'); break;
    case 'CRIT': params.set('stock', 'critical'); break;
    case 'OUT':  params.set('stock', 'out'); break;
    default:     params.delete('stock'); break;
  }

  navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });
};

  // combined filter object passed to grid (expiry handled on backend, stock handled on frontend)
 const filter = useMemo(
  () => ({
    ...expiryKeyToFilter(expiryKey),
    stockLevel: stockKey,
    typeKey,
  }),
  [expiryKey, stockKey, typeKey]
);

  return (
    <Box sx={{ width:{lg:'100%',}, marginLeft: {xs:'8%', sm:'2%', lg:0},textAlign: 'center', p: 2, backgroundColor: 'white', borderRadius: 1 }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          width: '90%',
          margin: 'auto',
          py: '15px',
          pb: '32px',
        }}
      >
        <Typography variant="h3" sx={{ fontSize: {
      xs: '1.6rem',  // mobile
      sm: '2rem',    // small tablets
      md: '2.5rem',  // desktop
    },  fontWeight: 600 }}>
          Inventory
        </Typography>
        <CustomizedDialogs
        externalMode={restockRow ? 'RESTOCK' : 'ADD_NEW'}
        restockRow={restockRow}
        onClose={() => setRestockRow(null)}
         />
      </Box>

      {/* FILTERS */}
      <Box sx={{ width: '90%', margin: 'auto', mb: 2, textAlign: 'left' }}>
        {/* Expiry filters */}
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: 600, mb: 1, color: '#374151' }}
        >
          Filter by Expiry Date
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
          {EXPIRY_KEYS.map((f) => (
            <Button
              key={f.key}
              size="small"
              variant={expiryKey === f.key ? 'contained' : 'outlined'}
              onClick={() => handleExpiryChange(f.key)}
              sx={{
                borderRadius: 999,
                textTransform: 'none',
                fontWeight: 600,
                fontSize: 13,
                px: 2.2,
                py: 0.6,

                backgroundColor: expiryKey === f.key ? '#333' : '#f3f4f6',
                color: expiryKey === f.key ? 'white' : '#374151',
                borderColor: expiryKey === f.key ? '#2563eb' : '#d1d5db',

                // REMOVE SHADOW
  boxShadow: 'none',
  '&:hover': {
    backgroundColor: expiryKey !== f.key && '#333',
    color: expiryKey !== f.key && '#fff',
    boxShadow: 'none',
    border: 'none'
  },
  '&:active': { boxShadow: 'none' },
  '&.Mui-focusVisible': { boxShadow: 'none' },
              }}
            >
              {f.label}
            </Button>
          ))}
        </Box>

        {/* Stock filters */}
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: 600, mb: 1, color: '#374151' }}
        >
          Filter by Stock Level
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {STOCK_KEYS.map((f) => (
            <Button
              key={f.key}
              size="small"
              variant={stockKey === f.key ? 'contained' : 'outlined'}
              onClick={() => handleStockChange(f.key)}
              sx={{
                borderRadius: 999,
                textTransform: 'none',
                fontWeight: 600,
                fontSize: 13,
                px: 2.2,
                py: 0.6,

                backgroundColor: stockKey === f.key ? '#333' : '#f3f4f6',
                color: stockKey === f.key ? 'white' : '#374151',
                borderColor: stockKey === f.key ? '#2563eb' : '#d1d5db',

                // REMOVE SHADOW
              boxShadow: 'none',
              '&:hover': {
                backgroundColor: stockKey !== f.key && '#333',
                color: stockKey !== f.key && '#fff',
                boxShadow: 'none',
                border: 'none',
              },

              '&:active': { boxShadow: 'none' },
              '&.Mui-focusVisible': { boxShadow: 'none' },
              }}
            >
              {f.label}
            </Button>
          ))}
        </Box>
      </Box>

      {/* Grid */}
      <Box
        sx={{
          width: '90%',
          margin: 'auto',
          backgroundColor: '#f9fafb',
          // mt: 2,
          pb: 2,
        }}
      >
        <CommodityGrid 
        filter={filter}
        typeKey={typeKey}
        onTypeChange={setTypeKey}
        onRestockRow={(row) => setRestockRow(row)} />
      </Box>
    </Box>
  );
};

export default Inventory;

